import React, { useState } from "react";
import MUITextField, {
  TextFieldProps as MUITextFieldProps
} from "@mui/material/TextField";
import { InputAdornment } from "@mui/material";
import { IconButton } from "@pokemon/design-v2.icon-button";
import { useTranslation } from "react-i18next";

export interface TextFieldProps {
  /**
   * Required in order for accessibility features to work properly. Should be unique from other text fields on a page.
   */
  id: string;

  /**
   * Can be set to true to error without error text. Field will always display in an error state if errorText is provided.
   */
  error?: boolean;

  /**
   * Text to display below field when in an error state. If provided, the text field will display as errored.
   */
  errorText?: string;

  /**
   * Placeholder text.
   */
  placeholder?: string;

  /**
   * Variant of the field to be displayed. Options are: 'outlined' | 'filled' | 'standard'. Will default to standard.
   */
  variant?: "outlined" | "filled" | "standard";

  /**
   * Text field color. Defaults to primary.
   */
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";

  /**
   * Variant of the field to be displayed. Options are: 'outlined' | 'filled' | 'standard'. Will default to standard.
   */
  disabled?: boolean;

  /**
   * Display the input with multiline. Defaults to false.
   */
  multiline?: boolean;

  /**
   * Numer of rows to display when multiline is set to true. (Field will grow if not set).
   */
  rows?: number;

  /**
   * Maximum number of rows to display when multiline option is set to true.
   */
  maxRows?: number;

  /**
   * Minumum number of rows to display when multiline option is set to true.
   */
  minRows?: number;

  /**
   * Display the show/hide toggle for passwords or other hidden fields.
   */
  password?: boolean;

  /**
   * Start the password in a visible state.
   */
  startsAsVisible?: boolean;
}

export function TextField({
  id,
  error,
  errorText,
  variant,
  color,
  disabled,
  multiline,
  rows,
  maxRows,
  minRows,
  password = false,
  startsAsVisible,
  ...textFieldProps
}: TextFieldProps & MUITextFieldProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(startsAsVisible || false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const visibilityToggle = (
    <InputAdornment position="end">
      <IconButton
        aria-label={t(
          "toggle_password_visibility",
          "Toggle Password Visibility"
        )}
        data-testid="toggle-button"
        onClick={handleClickShowPassword}
        onMouseDown={handleMouseDownPassword}
      >
        {showPassword ? (
          <img
            alt={t("show_password", "Show")}
            src="https://static.pokemon.com/images/show_password.svg"
            width={30}
          />
        ) : (
          <img
            alt={t("hide_password", "Hide")}
            src="https://static.pokemon.com/images/hide_password.svg"
            width={30}
          />
        )}
      </IconButton>
    </InputAdornment>
  );

  return (
    <MUITextField
      id={id}
      error={!!errorText || error}
      helperText={errorText}
      variant={variant}
      color={color}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      maxRows={maxRows}
      minRows={minRows}
      type={password && showPassword ? "password" : "text"}
      InputProps={{
        inputProps: {
          "data-testid": id
        },
        endAdornment: password ? visibilityToggle : ""
      }}
      {...textFieldProps}
    />
  );
}
